import type { LearningSettings, LearningSnapshot } from '@oysterjs/smartfill-contracts';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSidebarSession } from '../bridge/session';
import { useKeyedBridgeAction } from './data';
import { displayError, type DisplayError } from '../bridge/errors';

interface LearningSession {
  snapshot: LearningSnapshot | null;
  error: DisplayError | null;
  settingsBusy: boolean;
  capabilities: readonly string[];
  reload: () => void;
  updateSettings: (settings: Partial<LearningSettings>) => Promise<void>;
}

const LearningContext = createContext<LearningSession | null>(null);

/** The worker owns authority; this provider holds only the current panel's display state. */
export function LearningSessionProvider({ children }: { children: ReactNode }) {
  const { client, snapshot: sidebar } = useSidebarSession();
  const scope = `${sidebar.stateRevisions.epoch}:${sidebar.tab.contextId}:${sidebar.tab.origin}:${sidebar.activeBucket?.bucketId ?? ''}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [stored, setStored] = useState<{ scope: string; snapshot: LearningSnapshot } | null>(null);
  const [supported, setSupported] = useState<{ scope: string; capabilities: string[] } | null>(
    null
  );
  const snapshot = stored?.scope === scope ? stored.snapshot : null;
  const [loadError, setLoadError] = useState<DisplayError | null>(null);
  const [reloads, setReloads] = useState(0);
  const generation = useRef(0);
  const live = useRef(false);
  const settingsAction = useKeyedBridgeAction<string>();

  useEffect(() => {
    live.current = true;
    setLoadError(null);
    // Subscribe first. A push is newer than any query already in flight.
    const unsubscribe = client.subscribe('learning.changed', (next) => {
      if (currentScope.current !== scope) return;
      generation.current++;
      setStored({ scope, snapshot: next });
      setLoadError(null);
    });
    const started = ++generation.current;
    void client
      .request('capabilities.get', {})
      .then((next) => {
        if (!live.current || currentScope.current !== scope) return null;
        setSupported({ scope, capabilities: next.capabilities });
        return client.request('learning.get', {});
      })
      .then(
        (next) => {
          if (
            next &&
            live.current &&
            generation.current === started &&
            currentScope.current === scope
          )
            setStored({ scope, snapshot: next });
        },
        (error) => {
          if (live.current && generation.current === started && currentScope.current === scope) {
            setLoadError(displayError(error, 'Could not load learning settings.'));
          }
        }
      );
    return () => {
      live.current = false;
      generation.current++;
      unsubscribe();
    };
  }, [client, reloads, scope]);

  const updateSettings = async (settings: Partial<LearningSettings>) => {
    await settingsAction.run(
      'settings',
      'Could not update learning. Nothing was changed on the form.',
      async () => {
        const started = ++generation.current;
        const next = await client.request('learning.settings.update', settings);
        if (!live.current || generation.current !== started || currentScope.current !== scope)
          return;
        setStored((current) =>
          current?.scope === scope
            ? { scope, snapshot: { ...current.snapshot, settings: next } }
            : current
        );
      }
    );
  };

  return (
    <LearningContext.Provider
      value={{
        snapshot,
        error: loadError ?? settingsAction.error,
        settingsBusy: settingsAction.busy !== null,
        capabilities: supported?.scope === scope ? supported.capabilities : [],
        reload: () => setReloads((value) => value + 1),
        updateSettings
      }}
    >
      {children}
    </LearningContext.Provider>
  );
}

export function useLearningSession(): LearningSession {
  const session = useContext(LearningContext);
  if (!session) throw new Error('Learning session requires the signed-in sidebar.');
  return session;
}
